import { useEffect } from "react";
import { useKpiTargetsStore } from "./useKpiTargetsStore";
import { useOrganizationStore } from "@/features/organization/hooks";
import { useParams } from "react-router-dom";
/**
 * Custom hook to manage framework data loading and synchronization
 * Consolidates multiple useEffect hooks for better performance and maintainability
 */
export const useFrameworkData = () => {
  const store = useKpiTargetsStore();
  const { selectedUnit } = useOrganizationStore();
  const { targetId } = useParams();

  const {
    activeCategory,
    frameworks,
    getCategories,
    getDisclosureCountByFramework,
    hasDisclosuresLoaded,
    isEnvOnly,
    loadDisclosuresFromApi,
    loadFrameworks,
    loadTargetsFromApi,
    loadingFrameworks,
    selectedFramework,
    selectedYear,
    targetSettingsList,
    setActiveCategory,
    setOrg,
    setSelectedFramework,
  } = store;

  // Set organization ID when selected unit changes
  useEffect(() => {
    if (selectedUnit?.id) {
      setOrg(selectedUnit.id);
    }
  }, [selectedUnit?.id, setOrg]);

  // Enforce Environment category for env-only users
  useEffect(() => {
    if (isEnvOnly && activeCategory !== "Environmental") {
      setActiveCategory("Environmental");
    }
  }, [isEnvOnly, activeCategory, setActiveCategory]);

  // Load frameworks once on mount
  useEffect(() => {
    loadFrameworks();
  }, [loadFrameworks]);

  // Load initial "All" disclosures once for framework counts
  useEffect(() => {
    loadDisclosuresFromApi("All", "All");
  }, [loadDisclosuresFromApi]);

  // Load filtered disclosures when framework or category changes
  useEffect(() => {
    if (selectedFramework !== "All" || activeCategory !== "All") {
      loadDisclosuresFromApi(selectedFramework, activeCategory);
    }
  }, [loadDisclosuresFromApi, selectedFramework, activeCategory]);

  // Load targets when year, framework, or category changes
  useEffect(() => {
    if (targetId) {
      loadTargetsFromApi(
        targetId,
        selectedYear,
        selectedFramework,
        activeCategory,
      );
    }
  }, [
    loadTargetsFromApi,
    targetId,
    selectedYear,
    selectedFramework,
    activeCategory,
  ]);

  // Validate selected framework is still available after disclosures load
  useEffect(() => {
    if (!hasDisclosuresLoaded || selectedFramework === "All") return;

    const frameworkCounts = getDisclosureCountByFramework();
    const visibleFrameworks = frameworks.filter(
      (framework) => (frameworkCounts[framework.code] || 0) > 0,
    );

    const stillAvailable = visibleFrameworks.some(
      (framework) => framework.code === selectedFramework,
    );

    if (!stillAvailable) {
      setSelectedFramework(visibleFrameworks[0]?.code);
    }
  }, [
    hasDisclosuresLoaded,
    selectedFramework,
    frameworks,
    getDisclosureCountByFramework,
    setSelectedFramework,
  ]);

  // Compute visible categories
  const categories = getCategories(selectedFramework);
  const visibleCategories = isEnvOnly
    ? categories.filter((category) => category === "Environmental")
    : categories;

  // Compute visible frameworks
  const frameworkCounts = getDisclosureCountByFramework();
  const visibleFrameworks = hasDisclosuresLoaded
    ? frameworks.filter(
        (framework) => (frameworkCounts[framework.code] || 0) > 0,
      )
    : frameworks;

  return {
    visibleCategories,
    visibleFrameworks,
    frameworkCounts,
    loadingFrameworks,
  };
};
